import {
  FluidAlignment,
  FluidComponentDefinition,
  FluidContextMenuItemType,
  FluidContextMenuSection,
  FluidContextMenuToggle,
  FluidEditableCellConfig,
  FluidFormElementType,
  FluidRowData,
  FluidTableHeader,
  FluidTableHeaderType,
} from '@lmig/fluid-core';
import { FluidTableTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-table-translation-options.type';

import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { FluidTable, FluidTranslatableTableHeader } from '../fluid-table';
import { COLUMN_VISIBILITY } from './filter/fluid-filter.constants';
import { getTruncatedValue } from './fluid-table-data-management.utils';

/**
 * Get column toggle list for the context menu
 * @param headers
 * @param i18nStore
 */
export const columnToggles = (
  headers: FluidTranslatableTableHeader[],
  i18nStore?: FluidI18nStore
) => {
  return headers.reduce(
    (
      columns: FluidContextMenuToggle[],
      header: FluidTranslatableTableHeader
    ) => {
      return header.label
        ? [
            ...columns,
            {
              id: header.dataPath,
              label:
                i18nStore?.translate(header.fluidTranslate?.label) ||
                header.label,
              selected: header.visible,
            } as FluidContextMenuToggle,
          ]
        : columns;
    },
    []
  );
};

export const getColumnVisibilityMenu = (
  headers: FluidTranslatableTableHeader[],
  i18nStore?: FluidI18nStore,
  fluidTranslate?: FluidTableTranslationOptions
): FluidContextMenuSection => ({
  title:
    i18nStore?.translate(fluidTranslate?.columnVisibility?.title) || 'Columns',
  id: COLUMN_VISIBILITY,
  type: FluidContextMenuItemType.TOGGLE,
  toggles: columnToggles(headers, i18nStore),
  sectionHeight: '400px',
});

export const getFixedWidthHeader = (
  header: FluidTableHeader,
  headers: FluidTableHeader[]
): number => {
  const total = headers.reduce(
    (runningTotal: number, curr: FluidTableHeader) => {
      runningTotal += curr.columnWidth || 1;
      return runningTotal;
    },
    0
  );
  return Math.floor((header.columnWidth / total) * 100);
};

/**
 * Returns cell alignment based on data type, unless explicitly overridden
 * by the cellAlignmnent property.
 * @param header
 * @param splitHeadersExist
 */
export const getCellAlignment = (
  header: FluidTableHeader,
  splitHeadersExist?: boolean
): FluidAlignment => {
  if (header.cellAlignment) return header.cellAlignment;
  if (
    header.editableCellConfig &&
    (header.editableCellConfig as FluidEditableCellConfig).elementType ===
      FluidFormElementType.TOGGLE
  ) {
    header.type = FluidTableHeaderType.TOGGLE;
  }
  if (splitHeadersExist && (header.splitDataPoints || []).length > 0) {
    return FluidAlignment.CENTER;
  }
  switch (header.type) {
    case FluidTableHeaderType.CURRENCY:
    case FluidTableHeaderType.NUMBER:
      return FluidAlignment.RIGHT;
    case FluidTableHeaderType.TOGGLE:
    case FluidTableHeaderType.SELECTABLE:
    case FluidTableHeaderType.DATE:
    case FluidTableHeaderType.ACTION:
      return FluidAlignment.CENTER;
    default:
      return FluidAlignment.LEFT;
  }
};

export function getComponentDefinitionForCell(
  header: FluidTableHeader,
  dataItem: FluidRowData,
  host: Partial<FluidTable>
): FluidComponentDefinition | undefined {
  const { renderComponent, truncationOptions } = header;
  if (!renderComponent) return undefined;
  const componentDefinition = renderComponent(dataItem);
  if (!componentDefinition) return undefined;
  if (componentDefinition.props) {
    componentDefinition.props.theme =
      componentDefinition.props.theme || host.theme;
    componentDefinition.props.overrideGlobalTheme =
      componentDefinition.props.overrideGlobalTheme || host.overrideGlobalTheme;
  }
  if (truncationOptions && !!componentDefinition.content) {
    componentDefinition.content = getTruncatedValue(
      truncationOptions,
      null,
      null,
      componentDefinition.content
    );
  }
  return componentDefinition;
}
